import {lazy, memo, Suspense, useContext, useEffect, useState} from 'react'

import PropTypes from 'prop-types'

import Context from '@s-ui/react-context'
import {useEventListener} from '@s-ui/react-hooks'
import MoleculeModal from '@s-ui/react-molecule-modal'
import IconClose from '@adv-mt/mt-svg-icons/lib/Close'
import {useAnalyticsTrack} from '@adv-mt/mt-tracking-use-analytics'

import NotifyToGigya from './notifyToGigya.js'

const GIGYA_MODAL_WRAPPER_CLASS = 'mt-GigyaModalWrapper'

const modalCloseIcon = <IconClose color="primary" render="lazy" size="medium" />

const GigyaLoader = lazy(() => import('@adv-mt/mt-gigya-loader'))

const GigyaSocialNotifier = ({
  loginConfig,
  onModalClose = () => {},
  onAuthentication = () => {},
  notified = false,
  origin
}) => {
  const {domain} = useContext(Context)
  const config = domain.get('config')

  const {track} = useAnalyticsTrack()

  const GIGYA_API_KEY = config.get('GIGYA_API_KEY')

  const {GIGYA_SCREEN_CONTAINER_ID} = config.get('login')

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isGigyaDialogFinished, setIsGigyaDialogFinished] = useState(false)

  useEventListener('onBeforeScreenLoadGigyaEvent', () => {
    !isGigyaDialogFinished && setIsModalOpen(true)
  })

  useEffect(() => {
    if (isGigyaDialogFinished) {
      setIsModalOpen(false)
      onModalClose()
    }
  }, [isGigyaDialogFinished]) // eslint-disable-line

  const handleOnScreenFinished = () => {
    setIsGigyaDialogFinished(true)
  }

  const handleOnModalClose = async () => {
    handleOnScreenFinished()
    const trackingErrorEvent = await domain.get('get_event_tracking_data_use_case').execute({
      event: 'Signed In Error',
      origin,
      context: 'gigya screens modal dismissed'
    })
    track(trackingErrorEvent)
  }

  return !notified ? (
    <>
      <Suspense fallback={<div />}>
        <GigyaLoader
          gigyaApiKey={GIGYA_API_KEY}
          render={() => (
            <NotifyToGigya
              loginConfig={loginConfig}
              onScreenFinished={handleOnScreenFinished}
              onAuthentication={onAuthentication}
              origin={origin}
            />
          )}
        />
      </Suspense>

      <MoleculeModal
        centerVertically
        closeOnOutsideClick
        closeOnEscKeyDown
        fitWindow
        header={<span />}
        iconClose={modalCloseIcon}
        onClose={handleOnModalClose}
        isOpen={isModalOpen}
      >
        <div id={GIGYA_SCREEN_CONTAINER_ID} className={GIGYA_MODAL_WRAPPER_CLASS} />
      </MoleculeModal>
    </>
  ) : null
}

GigyaSocialNotifier.displayName = 'GigyaSocialNotifier'
GigyaSocialNotifier.propTypes = {
  loginConfig: PropTypes.object.isRequired,
  onModalClose: PropTypes.func,
  onAuthentication: PropTypes.func,
  notified: PropTypes.bool,
  origin: PropTypes.string
}

export default memo(GigyaSocialNotifier)
